import { useState } from "react"
import { useCreatePaymentIntentMutation, useFinalizeTopUpMutation } from "./api"
import getStripe from "./stripe"
import type { TopUpAction } from "./model"
import { ensure } from "@/utils"
import { useT } from "@/i18n"
import { Button } from "@/ui/components/ui/button"
import { BusySpinner } from "@/ui/composed"

type Props = {
  readonly credits: number
  readonly payLabel: string
  readonly action: TopUpAction
  /** Called with the paid PaymentIntent so TopUp can await its settlement. */
  readonly onSubmitted: (paymentIntentId: string) => void
}

/**
 * Repeat purchase: the saved card is attached server-side, so there is no
 * Payment Element to mount — one tap confirms the intent (SCA overlay only if
 * the bank demands it). Finalize eagerly settles; the outcome itself arrives
 * over realtime, which TopUp awaits once this hands off the paid PaymentIntent.
 */
export default function RepeatPurchasePanel({
  credits,
  payLabel,
  action,
  onSubmitted,
}: Props) {
  const { t } = useT("money")
  const [createPaymentIntent] = useCreatePaymentIntentMutation()
  const [finalizeTopUp] = useFinalizeTopUpMutation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pay = async () => {
    setError(null)
    setBusy(true)
    try {
      const stripe = ensure(await getStripe())

      const { clientSecret, paymentIntentId } = await createPaymentIntent({
        credits,
        action,
        // Already on file from the first purchase; nothing new to capture.
        taxId: null,
        businessName: null,
      }).unwrap()

      const { error: confirmError } = await stripe.confirmPayment({
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`,
        },
        redirect: "if_required",
      })
      if (confirmError) {
        setError(confirmError.message ?? t("topUp.genericError"))

        return
      }

      await finalizeTopUp({ paymentIntentId }).unwrap()
      onSubmitted(paymentIntentId)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {!!error && <p className="text-sm text-destructive">{error}</p>}
      <Button onClick={pay} disabled={busy}>
        <BusySpinner busy={busy} />
        {payLabel}
      </Button>
    </div>
  )
}
